import React from 'react';
import { Pivot, PivotItem, Text, IconButton, Icon, DirectionalHint } from '@fluentui/react';
import ClubImageCircle from '../Common/ClubImageCircle';
import { todosContraTodos, Torneo, Partido, Planilla } from '../../system/SystemClient';
import { genkey } from '../../common/constants';
import { useBoolean, useId } from '@fluentui/react-hooks';
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Callout, Link, mergeStyleSets, FontWeights } from '@fluentui/react';
import { DateTime } from 'luxon';
import { HIDDEN_LIMIT_UP, PendienteConfirmacionCallout, PlanillasCallout } from './PartidoFixture';

interface IPartidoMobile {
  partido: Partido;
  openCalloutOrOpenPDF: (planillas: Planilla[], openCallout: () => void) => void;
  openCalloutOrOpenVideo: (planillas: Planilla[], openCallout: () => void) => void;
  openMap: (campoJuegoCoords: string) => void;
  openMapWithDireccion: (campoJuegoDireccion: string) => void;
  getStylesBy: (id: string) => React.CSSProperties | undefined;
}

const styles = mergeStyleSets({
  button: {
    width: 130,
  },
  callout: {
    width: 320,
    maxWidth: '90%',
    padding: '6px 10px',
  },
  title: {
    marginBottom: 12,
    fontWeight: FontWeights.semilight,
  },
  link: {
    display: 'block',
    marginTop: 20,
  },
});

export const PartidoMobile = ({
  partido,
  openCalloutOrOpenPDF,
  openCalloutOrOpenVideo,
  openMap,
  getStylesBy,
  openMapWithDireccion,
}: IPartidoMobile) => {
  const [isCalloutPlanillaVisibleMobile, { toggle: toggleIsCalloutVisibleMobile }] = useBoolean(false);
  const buttonIdMobile = useId(genkey());
  const labelIdMobile = useId(genkey());
  const descriptionIdMobile = useId(genkey());

  const [
    isCalloutPendienteConfirmacionMobile,
    { toggle: toggleIsCalloutPendienteConfirmacionVisibleMobile },
  ] = useBoolean(false);
  const buttonIdPendienteConfirmacionMobile = useId(genkey());
  const labelIdPendienteConfirmacionMobile = useId(genkey());
  const descriptionIdPendienteConfirmacionMobile = useId(genkey());

  const [isCalloutCampoJuegoVisibleMobile, { toggle: toggleIsCalloutCampoJuegoVisibleMobile }] =
    useBoolean(false);
  const labelIdCampoJuegoMobile = useId(genkey());
  const descriptionIdCampoJuegoMobile = useId(genkey());
  const buttonIdCampoJuegoMobile = useId(genkey());

  const [isCalloutArbitrosVisibleMobile, { toggle: toggleIsCalloutArbitrosVisibleMobile }] =
    useBoolean(false);
  const labelIdArbitrosMobile = useId(genkey());
  const descriptionIdArbitrosMobile = useId(genkey());
  const buttonIdArbitrosMobile = useId(genkey());

  return (
    <div
      id="viewMobile"
      className={`${HIDDEN_LIMIT_UP} mt-1`}
      style={{ borderTop: '1px solid #e6e6e6', marginTop: 5 }}
    >
      <div className="ms-Grid-col ms-sm7 text-overflow text-left" style={{ marginTop: 5 }}>
        <Text className="mb-1" variant="small">
          <Icon iconName="Calendar" style={{ marginLeft: 5 }} className="text-icon-small"></Icon>
          {DateTime.fromFormat(partido.horario, 'yyyy/LL/dd HH:mm:ss')
            .setLocale('es')
            .toFormat('ccc dd LLLL HH:mm')}
        </Text>
      </div>
      <div className="ms-Grid-col ms-sm5 text-overflow text-left" style={{ marginTop: 5 }}>
        <PrimaryButton
          iconProps={{ iconName: 'PublishCourse' }}
          title="Ver arbitros"
          id={buttonIdArbitrosMobile}
          className="float-right"
          onClick={toggleIsCalloutArbitrosVisibleMobile}
          styles={{
            root: {
              border: '1px solid #00b2ff',
              background: '#00b2ff',
              borderRadius: 25,
              padding: 0,
              minWidth: 23,
              height: 26,
              marginLeft: 5,
            },
          }}
        ></PrimaryButton>
        <PrimaryButton
          iconProps={{ iconName: 'POI' }}
          title="Ver estadio"
          id={buttonIdCampoJuegoMobile}
          className="float-right"
          onClick={toggleIsCalloutCampoJuegoVisibleMobile}
          styles={{
            root: {
              border: '1px solid #009454',
              background: '#009454',
              borderRadius: 25,
              padding: 0,
              minWidth: 23,
              height: 26,
              marginLeft: 5,
            },
          }}
        ></PrimaryButton>

        {partido.pendingConfirmation ? (
          <PrimaryButton
            iconProps={{ iconName: 'AccountActivity' }}
            id={buttonIdPendienteConfirmacionMobile}
            onClick={() => toggleIsCalloutPendienteConfirmacionVisibleMobile()}
            styles={{
              root: {
                borderRadius: 25,
                padding: 0,
                minWidth: 23,
                height: 26,
                marginLeft: 5,
                border: '1px solid #f79c25',
                background: '#f79c25',
              },
            }}
            className="float-right"
          ></PrimaryButton>
        ) : (
          <PrimaryButton
            iconProps={{ iconName: 'AccountActivity' }}
            id={buttonIdMobile}
            onClick={() => openCalloutOrOpenPDF(partido.planillas, toggleIsCalloutVisibleMobile)}
            styles={{ root: { borderRadius: 25, padding: 0, minWidth: 23, height: 26, marginLeft: 5 } }}
            title="Ver planilla"
            className="float-right"
            disabled={partido.planillas.filter((planilla) => planilla.pdf).length === 0}
          ></PrimaryButton>
        )}

        {partido.planillas.some((planilla) => planilla.url_transmision) && (
          <PrimaryButton
            iconProps={{ iconName: 'Video' }}
            className="float-right "
            title="Ver transmisión"
            id={buttonIdMobile + 'M'}
            onClick={() => openCalloutOrOpenVideo(partido.planillas, toggleIsCalloutVisibleMobile)}
            styles={{
              root: {
                border: '1px solid #f79c25',
                background: '#f79c25',
                borderRadius: 25,
                padding: 0,
                minWidth: 23,
                height: 26,
              },
            }}
          ></PrimaryButton>
        )}

        {partido.playing && (
          <Text variant="small" className="float-right" style={{ position: 'relative', top: 5 }}>
            <Icon
              iconName="FullCircleMask"
              className="live-color blinking-icon  "
              style={{ position: 'relative', top: 2, marginRight: 4 }}
            ></Icon>
            En vivo
          </Text>
        )}

        <>
          {isCalloutPlanillaVisibleMobile && (
            <Callout
              ariaLabelledBy={labelIdMobile}
              ariaDescribedBy={descriptionIdMobile}
              role="dialog"
              gapSpace={0}
              target={`#${buttonIdMobile}`}
              onDismiss={toggleIsCalloutVisibleMobile}
              setInitialFocus
              className={styles.callout}
              directionalHint={DirectionalHint.leftCenter}
            >
              <PlanillasCallout partido={partido} labelId={labelIdMobile}></PlanillasCallout>
            </Callout>
          )}
        </>

        <>
          {isCalloutPendienteConfirmacionMobile && (
            <Callout
              ariaLabelledBy={labelIdPendienteConfirmacionMobile}
              ariaDescribedBy={descriptionIdPendienteConfirmacionMobile}
              role="dialog"
              gapSpace={0}
              target={`#${buttonIdPendienteConfirmacionMobile}`}
              onDismiss={toggleIsCalloutPendienteConfirmacionVisibleMobile}
              setInitialFocus
              className={styles.callout}
              directionalHint={DirectionalHint.leftCenter}
            >
              <PendienteConfirmacionCallout></PendienteConfirmacionCallout>
            </Callout>
          )}
        </>

        <>
          {isCalloutCampoJuegoVisibleMobile && (
            <Callout
              ariaLabelledBy={labelIdCampoJuegoMobile}
              ariaDescribedBy={descriptionIdCampoJuegoMobile}
              role="dialog"
              gapSpace={0}
              target={`#${buttonIdCampoJuegoMobile}`}
              onDismiss={toggleIsCalloutCampoJuegoVisibleMobile}
              setInitialFocus
              className={styles.callout}
              directionalHint={DirectionalHint.leftCenter}
            >
              <span style={{ margin: 10, fontSize: 14, fontWeight: 500 }}>{partido.campoJuegoNombre}</span>
              {partido.campoJuegoCoords && (
                <span
                  className="link-styles"
                  onClick={() => openMap(partido.campoJuegoCoords)}
                  style={{ marginLeft: 10, fontSize: 12, fontWeight: 500, display: 'block' }}
                >
                  Ver dirección
                </span>
              )}
            </Callout>
          )}
        </>

        <>
          {isCalloutArbitrosVisibleMobile && (
            <Callout
              ariaLabelledBy={labelIdArbitrosMobile}
              ariaDescribedBy={descriptionIdArbitrosMobile}
              role="dialog"
              gapSpace={0}
              target={`#${buttonIdArbitrosMobile}`}
              onDismiss={toggleIsCalloutArbitrosVisibleMobile}
              setInitialFocus
              className={styles.callout}
              directionalHint={DirectionalHint.leftCenter}
            >
              <div>
                <div>
                  <span style={{ margin: 10, fontSize: 14, fontWeight: 500 }}>Arbitros</span>
                </div>
                <div>
                  <span style={{ margin: 10, fontSize: 14, fontWeight: 400 }}>
                    {partido.arbitros ? partido.arbitros : 'Aun no designados'}
                  </span>
                </div>
              </div>
            </Callout>
          )}
        </>
      </div>

      <div className="ms-Grid-col ms-sm12" style={{ minHeight: 30 }}>
        <TeamMobile
          escudo={partido.escudoImagePathLocal}
          nombre={partido.nombreLocal}
          goles={partido.played || partido.playing ? partido.golesLocal : '-'}
          winner={partido.golesLocal > partido.golesVisitante}
          stylesHighlight={getStylesBy(partido.idClubLocal)}
        ></TeamMobile>
        <TeamMobile
          escudo={partido.escudoImagePathVisitante}
          nombre={partido.nombreVisitante}
          goles={partido.played || partido.playing ? partido.golesVisitante : '-'}
          winner={partido.golesVisitante > partido.golesLocal}
          stylesHighlight={getStylesBy(partido.idClubVisitante)}
        ></TeamMobile>
      </div>
    </div>
  );
};

const TeamMobile = (props: {
  escudo: string;
  nombre: string;
  goles: string;
  winner: boolean;
  stylesHighlight: React.CSSProperties | undefined;
}) => {
  return (
    <div className="ms-Grid-row mt-1">
      <div className="ms-Grid-col ms-sm1 text-overflow" style={{ minHeight: 30 }}>
        <ClubImageCircle escudo={props.escudo} width={25} height={25}></ClubImageCircle>
      </div>
      <div className="ms-Grid-col ms-sm9 text-overflow" style={{ minHeight: 30 }}>
        <Text
          className={`mb-1  text-overflow nombre-local text-left ${
            props.winner ? ' nombre-equipo-fixture-mobile-winner' : ' nombre-equipo-fixture-mobile'
          }`}
          variant="medium"
          style={props.stylesHighlight}
        >
          {props.nombre}
        </Text>
      </div>
      <div className={`ms-Grid-col ms-sm2 text-overflow }`} style={{ minHeight: 30 }}>
        <div className={`${props.winner ? 'winner-mobile' : 'loser-mobile'}`}>
          {props.goles}
          {/* {props.winner && <Icon iconName="ChevronLeftMed"></Icon>} */}
        </div>
      </div>
    </div>
  );
};
